import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, subDays, subMonths, subYears } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { apiService, API_ENDPOINTS } from "../../../config/api";
import {
  ParticipantDetailDay,
  ParticipantDetailResponse,
  ParticipantOption,
  ParticipantOptionsResponse,
} from "./types";

type MetricKey =
  | "stepsTotal"
  | "caloriesTotal"
  | "distanceTotal"
  | "activeMinutesLight"
  | "activeMinutesModerate"
  | "activeMinutesVigorous"
  | "azmFatBurn"
  | "azmCardio"
  | "azmPeak"
  | "sleepDuration"
  | "sleepEfficiency"
  | "restingHr"
  | "hrMinutesRecorded"
  | "hrvRmssd"
  | "hrvRmssdCv"
  | "vo2Max"
  | "spo2Avg"
  | "spo2Min"
  | "spo2Max"
  | "breathingRateAvg"
  | "weight"
  | "bodyFat"
  | "bmi"
  | "skinTemperature"
  | "coreTemperature"
  | "waterIntake"
  | "dataQualityScore";

type SelectedMetricKey = MetricKey | "";

type InsightTone =
  | "risk"
  | "caution"
  | "good"
  | "optimal"
  | "neutral";

type BaselinePreference = "higher" | "lower" | "neutral";

type MetricBand = {
  from?: number;
  to?: number;
  color: string;
  label: string;
  rangeLabel: string;
  meaning: string;
  tone: InsightTone;
};

type MetricDefinition = {
  key: MetricKey;
  label: string;
  shortLabel: string;
  description: string;
  color: string;
  formatter: (value: number) => string;
  axisFormatter?: (value: number) => string;
  accessor: (day: ParticipantDetailDay) => number | null | undefined;
  fixedDomain?: [number, number];
  bands?: MetricBand[];
  baselinePreference?: BaselinePreference;
  absoluteRangeNote?: string;
  baselineNote?: string;
};

type VisualizationRequest = {
  participantId: string;
  metricKey: MetricKey;
  startDate: string;
  endDate: string;
};

type ZoomRange = {
  startIndex: number;
  endIndex: number;
};

type MetricChartPoint = {
  date: string;
  value: number | null;
  rawIndex: number;
};

type MetricInsight = {
  title: string;
  label: string;
  detail: string;
  tone: InsightTone;
};

const METRIC_SELECT_PLACEHOLDER = "Choose metric";

const METRIC_DEFINITIONS: Record<MetricKey, MetricDefinition> = {
  stepsTotal: {
    key: "stepsTotal",
    label: "Steps Total",
    shortLabel: "Steps",
    description: "Daily total steps from Fitbit summary data.",
    color: "#0f766e",
    formatter: (value) => `${value.toFixed(0)}`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.stepsTotal,
    baselinePreference: "higher",
    absoluteRangeNote:
      "Step zones are goal-based for wearables, not diagnostic thresholds.",
    bands: [
      {
        to: 4000,
        color: "rgba(220, 38, 38, 0.12)",
        label: "Low",
        rangeLabel: "< 4,000 steps",
        meaning: "Low daily movement",
        tone: "risk",
      },
      {
        from: 4000,
        to: 8000,
        color: "rgba(245, 158, 11, 0.12)",
        label: "Moderate",
        rangeLabel: "4,000 to 8,000 steps",
        meaning: "Moderate daily movement",
        tone: "caution",
      },
      {
        from: 8000,
        to: 12000,
        color: "rgba(16, 185, 129, 0.12)",
        label: "Good",
        rangeLabel: "8,000 to 12,000 steps",
        meaning: "Good daily movement",
        tone: "good",
      },
      {
        from: 12000,
        color: "rgba(59, 130, 246, 0.10)",
        label: "High",
        rangeLabel: "> 12,000 steps",
        meaning: "Very active day",
        tone: "optimal",
      },
    ],
  },
  caloriesTotal: {
    key: "caloriesTotal",
    label: "Calories Total",
    shortLabel: "Calories",
    description: "Daily total calories from Fitbit summary data.",
    color: "#d97706",
    formatter: (value) => `${value.toFixed(0)} kcal`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.caloriesTotal,
    baselinePreference: "neutral",
    absoluteRangeNote:
      "Daily calories depend heavily on body size, age, sex, and device estimation. Use baseline and trend rather than universal good or bad cutoffs.",
  },
  distanceTotal: {
    key: "distanceTotal",
    label: "Distance Total",
    shortLabel: "Distance",
    description: "Daily total distance from Fitbit summary data.",
    color: "#0891b2",
    formatter: (value) => `${value.toFixed(2)} mi`,
    axisFormatter: (value) => `${value.toFixed(1)}`,
    accessor: (day) => day.distanceTotal,
    baselinePreference: "higher",
    absoluteRangeNote:
      "Distance is best interpreted as an activity goal and personal pattern, not a physiological risk threshold.",
  },
  activeMinutesLight: {
    key: "activeMinutesLight",
    label: "Light Active Minutes",
    shortLabel: "Light Active Min",
    description: "Daily light active minutes.",
    color: "#65a30d",
    formatter: (value) => `${value.toFixed(0)} min`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.activeMinutesLight,
    baselinePreference: "higher",
    absoluteRangeNote:
      "Light activity minutes vary a lot by routine, so relative change is usually more informative than a universal good or bad cutoff.",
  },
  activeMinutesModerate: {
    key: "activeMinutesModerate",
    label: "Moderate Active Minutes",
    shortLabel: "Moderate Active Min",
    description: "Daily moderate active minutes.",
    color: "#16a34a",
    formatter: (value) => `${value.toFixed(0)} min`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.activeMinutesModerate,
    baselinePreference: "higher",
    absoluteRangeNote:
      "These zones use daily equivalents of adult weekly physical activity guidance.",
    bands: [
      {
        to: 10,
        color: "rgba(220, 38, 38, 0.12)",
        label: "Low",
        rangeLabel: "< 10 min",
        meaning: "Below daily moderate-activity target",
        tone: "risk",
      },
      {
        from: 10,
        to: 22,
        color: "rgba(245, 158, 11, 0.12)",
        label: "Moderate",
        rangeLabel: "10 to 22 min",
        meaning: "Approaching daily moderate-activity target",
        tone: "caution",
      },
      {
        from: 22,
        to: 45,
        color: "rgba(16, 185, 129, 0.12)",
        label: "Good",
        rangeLabel: "22 to 45 min",
        meaning: "Meets daily equivalent of weekly guidance",
        tone: "good",
      },
      {
        from: 45,
        color: "rgba(59, 130, 246, 0.10)",
        label: "High",
        rangeLabel: "> 45 min",
        meaning: "High daily moderate activity",
        tone: "optimal",
      },
    ],
  },
  activeMinutesVigorous: {
    key: "activeMinutesVigorous",
    label: "Vigorous Active Minutes",
    shortLabel: "Vigorous Active Min",
    description: "Daily vigorous active minutes.",
    color: "#15803d",
    formatter: (value) => `${value.toFixed(0)} min`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.activeMinutesVigorous,
    baselinePreference: "higher",
    absoluteRangeNote:
      "These zones use daily equivalents of adult weekly vigorous-activity guidance.",
    bands: [
      {
        to: 5,
        color: "rgba(220, 38, 38, 0.12)",
        label: "Low",
        rangeLabel: "< 5 min",
        meaning: "Very little vigorous activity",
        tone: "risk",
      },
      {
        from: 5,
        to: 11,
        color: "rgba(245, 158, 11, 0.12)",
        label: "Moderate",
        rangeLabel: "5 to 11 min",
        meaning: "Approaching daily vigorous-activity target",
        tone: "caution",
      },
      {
        from: 11,
        to: 25,
        color: "rgba(16, 185, 129, 0.12)",
        label: "Good",
        rangeLabel: "11 to 25 min",
        meaning: "Meets daily equivalent of weekly guidance",
        tone: "good",
      },
      {
        from: 25,
        color: "rgba(59, 130, 246, 0.10)",
        label: "High",
        rangeLabel: "> 25 min",
        meaning: "High daily vigorous activity",
        tone: "optimal",
      },
    ],
  },
  azmFatBurn: {
    key: "azmFatBurn",
    label: "Active Zone Minutes Fat Burn",
    shortLabel: "AZM Fat Burn",
    description: "Daily fat burn active zone minutes.",
    color: "#a16207",
    formatter: (value) => `${value.toFixed(0)} min`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.azmFatBurn,
  },
  azmCardio: {
    key: "azmCardio",
    label: "Active Zone Minutes Cardio",
    shortLabel: "AZM Cardio",
    description: "Daily cardio active zone minutes.",
    color: "#ea580c",
    formatter: (value) => `${value.toFixed(0)} min`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.azmCardio,
  },
  azmPeak: {
    key: "azmPeak",
    label: "Active Zone Minutes Peak",
    shortLabel: "AZM Peak",
    description: "Daily peak active zone minutes.",
    color: "#dc2626",
    formatter: (value) => `${value.toFixed(0)} min`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.azmPeak,
  },
  sleepDuration: {
    key: "sleepDuration",
    label: "Sleep Duration",
    shortLabel: "Sleep Duration",
    description: "Daily sleep duration in minutes.",
    color: "#2563eb",
    formatter: (value) => `${value.toFixed(0)} min`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.sleepDuration,
    baselinePreference: "neutral",
    absoluteRangeNote:
      "Sleep duration zones are broad adult wearable guidance. Trends and consistency matter more than one night alone.",
    bands: [
      {
        to: 360,
        color: "rgba(220, 38, 38, 0.12)",
        label: "Poor",
        rangeLabel: "< 6 h",
        meaning: "Short sleep duration",
        tone: "risk",
      },
      {
        from: 360,
        to: 420,
        color: "rgba(245, 158, 11, 0.12)",
        label: "Okay",
        rangeLabel: "6 to 7 h",
        meaning: "Slight sleep deficit",
        tone: "caution",
      },
      {
        from: 420,
        to: 540,
        color: "rgba(16, 185, 129, 0.12)",
        label: "Good",
        rangeLabel: "7 to 9 h",
        meaning: "Recommended adult sleep duration",
        tone: "good",
      },
      {
        from: 540,
        color: "rgba(59, 130, 246, 0.10)",
        label: "High",
        rangeLabel: "> 9 h",
        meaning: "Long sleep duration",
        tone: "optimal",
      },
    ],
  },
  sleepEfficiency: {
    key: "sleepEfficiency",
    label: "Sleep Efficiency",
    shortLabel: "Sleep Efficiency",
    description: "Daily sleep efficiency percentage.",
    color: "#1d4ed8",
    formatter: (value) => `${value.toFixed(1)}%`,
    axisFormatter: (value) => `${Math.round(value)}%`,
    accessor: (day) => day.sleepEfficiency,
    fixedDomain: [0, 100],
    baselinePreference: "higher",
    absoluteRangeNote:
      "Sleep efficiency of about 85% or higher is commonly treated as a satisfactory sleep-research threshold.",
    bands: [
      {
        to: 75,
        color: "rgba(220, 38, 38, 0.12)",
        label: "Poor",
        rangeLabel: "< 75%",
        meaning: "Fragmented sleep",
        tone: "risk",
      },
      {
        from: 75,
        to: 85,
        color: "rgba(245, 158, 11, 0.12)",
        label: "Fair",
        rangeLabel: "75% to 85%",
        meaning: "Moderate sleep efficiency",
        tone: "caution",
      },
      {
        from: 85,
        to: 95,
        color: "rgba(16, 185, 129, 0.12)",
        label: "Good",
        rangeLabel: "85% to 95%",
        meaning: "Healthy sleep efficiency",
        tone: "good",
      },
      {
        from: 95,
        color: "rgba(59, 130, 246, 0.10)",
        label: "Excellent",
        rangeLabel: "> 95%",
        meaning: "Very efficient sleep",
        tone: "optimal",
      },
    ],
  },
  restingHr: {
    key: "restingHr",
    label: "Resting Heart Rate",
    shortLabel: "Resting HR",
    description: "Daily resting heart rate from Fitbit summary data.",
    color: "#b91c1c",
    formatter: (value) => `${value.toFixed(0)} bpm`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.restingHr,
    baselinePreference: "lower",
    absoluteRangeNote:
      "Resting heart rate is wearable-friendly to trend, and lower is generally better within a healthy context.",
    bands: [
      {
        to: 50,
        color: "rgba(59, 130, 246, 0.10)",
        label: "Athlete",
        rangeLabel: "< 50 bpm",
        meaning: "Very efficient resting heart rate",
        tone: "optimal",
      },
      {
        from: 50,
        to: 60,
        color: "rgba(16, 185, 129, 0.12)",
        label: "Good",
        rangeLabel: "50 to 60 bpm",
        meaning: "Fit resting heart rate",
        tone: "good",
      },
      {
        from: 60,
        to: 90,
        color: "rgba(245, 158, 11, 0.12)",
        label: "Normal",
        rangeLabel: "60 to 90 bpm",
        meaning: "Average adult resting range",
        tone: "caution",
      },
      {
        from: 90,
        color: "rgba(220, 38, 38, 0.12)",
        label: "High",
        rangeLabel: "> 90 bpm",
        meaning: "Elevated resting heart rate",
        tone: "risk",
      },
    ],
  },
  hrMinutesRecorded: {
    key: "hrMinutesRecorded",
    label: "HR Minutes Recorded",
    shortLabel: "HR Minutes",
    description:
      "Daily heart-rate minutes recorded from participant metadata.",
    color: "#7f1d1d",
    formatter: (value) => `${value.toFixed(0)} min`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.hrMinutesRecorded,
  },
  spo2Avg: {
    key: "spo2Avg",
    label: "SpO2 Analysis",
    shortLabel: "SpO2 Avg",
    description: "Daily average oxygen saturation from Fitbit summary data.",
    color: "#1f2937",
    formatter: (value) => `${value.toFixed(1)}%`,
    axisFormatter: (value) => `${Math.round(value)}%`,
    accessor: (day) => day.spo2Avg,
    fixedDomain: [85, 100],
    baselinePreference: "higher",
    absoluteRangeNote:
      "SpO2 is the strictest threshold-style wearable metric on this page. Treat low values as a signal to look closer, not as a diagnosis.",
    bands: [
      {
        from: 85,
        to: 92,
        color: "rgba(220, 38, 38, 0.12)",
        label: "Low",
        rangeLabel: "< 92%",
        meaning: "Concerning oxygen saturation",
        tone: "risk",
      },
      {
        from: 92,
        to: 95,
        color: "rgba(245, 158, 11, 0.12)",
        label: "Slightly Low",
        rangeLabel: "92% to 95%",
        meaning: "Below typical healthy range",
        tone: "caution",
      },
      {
        from: 95,
        to: 97,
        color: "rgba(16, 185, 129, 0.12)",
        label: "Normal",
        rangeLabel: "95% to 97%",
        meaning: "Healthy oxygen saturation",
        tone: "good",
      },
      {
        from: 97,
        to: 100,
        color: "rgba(59, 130, 246, 0.10)",
        label: "High Normal",
        rangeLabel: "97% to 100%",
        meaning: "Upper normal oxygen saturation",
        tone: "optimal",
      },
    ],
  },
  hrvRmssd: {
    key: "hrvRmssd",
    label: "HRV RMSSD",
    shortLabel: "HRV RMSSD",
    description: "Daily HRV RMSSD summary value.",
    color: "#0ea5e9",
    formatter: (value) => `${value.toFixed(1)} ms`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.hrvRmssd,
    baselinePreference: "higher",
    absoluteRangeNote:
      "HRV varies substantially by person and device context, so personal baseline matters more than the zone alone.",
    bands: [
      {
        to: 30,
        color: "rgba(220, 38, 38, 0.12)",
        label: "Low",
        rangeLabel: "< 30 ms",
        meaning: "Lower recovery / higher stress signal",
        tone: "risk",
      },
      {
        from: 30,
        to: 60,
        color: "rgba(245, 158, 11, 0.12)",
        label: "Moderate",
        rangeLabel: "30 to 60 ms",
        meaning: "Typical adult wearable range",
        tone: "caution",
      },
      {
        from: 60,
        to: 90,
        color: "rgba(16, 185, 129, 0.12)",
        label: "Good",
        rangeLabel: "60 to 90 ms",
        meaning: "Healthy recovery signal",
        tone: "good",
      },
      {
        from: 90,
        color: "rgba(59, 130, 246, 0.10)",
        label: "High",
        rangeLabel: "> 90 ms",
        meaning: "Very strong recovery signal",
        tone: "optimal",
      },
    ],
  },
  hrvRmssdCv: {
    key: "hrvRmssdCv",
    label: "HRV RMSSD CV",
    shortLabel: "HRV RMSSD CV",
    description: "Daily HRV RMSSD coefficient of variation.",
    color: "#0284c7",
    formatter: (value) => `${value.toFixed(1)}`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.hrvRmssdCv,
  },
  vo2Max: {
    key: "vo2Max",
    label: "VO2 Max",
    shortLabel: "VO2 Max",
    description: "Daily VO2 max estimate from Fitbit summary data.",
    color: "#0369a1",
    formatter: (value) => `${value.toFixed(1)}`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.vo2Max,
  },
  breathingRateAvg: {
    key: "breathingRateAvg",
    label: "Breathing Rate",
    shortLabel: "Breathing Rate",
    description: "Daily average breathing rate from Fitbit summary data.",
    color: "#ea580c",
    formatter: (value) => `${value.toFixed(1)}`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.breathingRateAvg,
    baselinePreference: "neutral",
    absoluteRangeNote:
      "Resting or sleep breathing rate is best read as a trend signal. Upward shifts can matter more than one isolated value.",
    bands: [
      {
        to: 12,
        color: "rgba(59, 130, 246, 0.10)",
        label: "Low",
        rangeLabel: "< 12 breaths/min",
        meaning: "Low resting breathing rate",
        tone: "optimal",
      },
      {
        from: 12,
        to: 17,
        color: "rgba(16, 185, 129, 0.12)",
        label: "Normal",
        rangeLabel: "12 to 17 breaths/min",
        meaning: "Typical resting range",
        tone: "good",
      },
      {
        from: 17,
        to: 20,
        color: "rgba(245, 158, 11, 0.12)",
        label: "Slightly High",
        rangeLabel: "17 to 20 breaths/min",
        meaning: "Elevated resting breathing rate",
        tone: "caution",
      },
      {
        from: 20,
        color: "rgba(220, 38, 38, 0.12)",
        label: "High",
        rangeLabel: "> 20 breaths/min",
        meaning: "High resting breathing rate",
        tone: "risk",
      },
    ],
  },
  spo2Min: {
    key: "spo2Min",
    label: "SpO2 Minimum",
    shortLabel: "SpO2 Min",
    description: "Daily minimum oxygen saturation from Fitbit summary data.",
    color: "#334155",
    formatter: (value) => `${value.toFixed(1)}%`,
    axisFormatter: (value) => `${Math.round(value)}%`,
    accessor: (day) => day.spo2Min,
    fixedDomain: [80, 100],
  },
  spo2Max: {
    key: "spo2Max",
    label: "SpO2 Maximum",
    shortLabel: "SpO2 Max",
    description: "Daily maximum oxygen saturation from Fitbit summary data.",
    color: "#475569",
    formatter: (value) => `${value.toFixed(1)}%`,
    axisFormatter: (value) => `${Math.round(value)}%`,
    accessor: (day) => day.spo2Max,
    fixedDomain: [85, 100],
  },
  weight: {
    key: "weight",
    label: "Weight",
    shortLabel: "Weight",
    description: "Daily weight from Fitbit summary data.",
    color: "#7c2d12",
    formatter: (value) => `${value.toFixed(1)} lb`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.weight,
  },
  bodyFat: {
    key: "bodyFat",
    label: "Body Fat",
    shortLabel: "Body Fat",
    description: "Daily body fat percentage from Fitbit summary data.",
    color: "#9a3412",
    formatter: (value) => `${value.toFixed(1)}%`,
    axisFormatter: (value) => `${Math.round(value)}%`,
    accessor: (day) => day.bodyFat,
    fixedDomain: [0, 100],
  },
  bmi: {
    key: "bmi",
    label: "BMI",
    shortLabel: "BMI",
    description: "Daily BMI from Fitbit summary data.",
    color: "#b45309",
    formatter: (value) => `${value.toFixed(1)}`,
    axisFormatter: (value) => `${Math.round(value)}`,
    accessor: (day) => day.bmi,
  },
  skinTemperature: {
    key: "skinTemperature",
    label: "Skin Temperature",
    shortLabel: "Skin Temp",
    description: "Daily skin temperature from Fitbit summary data.",
    color: "#be123c",
    formatter: (value) => `${value.toFixed(1)}°`,
    axisFormatter: (value) => `${value.toFixed(1)}`,
    accessor: (day) => day.skinTemperature,
  },
  coreTemperature: {
    key: "coreTemperature",
    label: "Core Temperature",
    shortLabel: "Core Temp",
    description: "Daily core temperature from Fitbit summary data.",
    color: "#e11d48",
    formatter: (value) => `${value.toFixed(1)}°`,
    axisFormatter: (value) => `${value.toFixed(1)}`,
    accessor: (day) => day.coreTemperature,
  },
  waterIntake: {
    key: "waterIntake",
    label: "Water Intake",
    shortLabel: "Water Intake",
    description: "Daily water intake from Fitbit summary data.",
    color: "#0284c7",
    formatter: (value) => `${value.toFixed(1)} oz`,
    axisFormatter: (value) => `${value.toFixed(0)}`,
    accessor: (day) => day.waterIntake,
  },
  dataQualityScore: {
    key: "dataQualityScore",
    label: "Data Quality Score",
    shortLabel: "Data Quality",
    description: "Daily data quality score computed during extraction.",
    color: "#4338ca",
    formatter: (value) => value.toFixed(2),
    axisFormatter: (value) => value.toFixed(1),
    accessor: (day) => day.dataQualityScore,
    fixedDomain: [0, 1],
  },
};

// Keep the visualization selector aligned with the currently supported
// researcher-facing Fitbit summary metrics in the extraction flow.
const SUPPORTED_METRIC_GROUPS: Array<{ label: string; keys: MetricKey[] }> = [
  {
    label: "Activity",
    keys: [
      "stepsTotal",
      "caloriesTotal",
      "distanceTotal",
      "activeMinutesLight",
      "activeMinutesModerate",
      "activeMinutesVigorous",
    ],
  },
  {
    label: "Sleep",
    keys: ["sleepDuration", "sleepEfficiency"],
  },
  {
    label: "Heart And Cardio",
    keys: ["restingHr", "hrvRmssd"],
  },
  {
    label: "SpO2 And Breathing",
    keys: ["spo2Avg", "breathingRateAvg"],
  },
];

const INSIGHT_TONE_STYLES: Record<
  InsightTone,
  {
    background: string;
    border: string;
    text: string;
  }
> = {
  risk: {
    background: "rgba(254, 242, 242, 0.96)",
    border: "rgba(248, 113, 113, 0.45)",
    text: "#b91c1c",
  },
  caution: {
    background: "rgba(255, 251, 235, 0.96)",
    border: "rgba(251, 191, 36, 0.45)",
    text: "#b45309",
  },
  good: {
    background: "rgba(240, 253, 244, 0.96)",
    border: "rgba(74, 222, 128, 0.42)",
    text: "#15803d",
  },
  optimal: {
    background: "rgba(239, 246, 255, 0.96)",
    border: "rgba(96, 165, 250, 0.42)",
    text: "#1d4ed8",
  },
  neutral: {
    background: "rgba(248, 250, 252, 0.96)",
    border: "rgba(148, 163, 184, 0.34)",
    text: "#334155",
  },
};

const formatDateLabel = (value: string, includeYear = false) => {
  try {
    return format(
      new Date(`${value}T00:00:00`),
      includeYear ? "MMM d, yyyy" : "MMM d",
    );
  } catch (_) {
    return value;
  }
};

const formatTimestampLabel = (value: string | null | undefined) => {
  if (!value) return "Not available";

  try {
    return format(new Date(value), "MMM d, yyyy h:mm a");
  } catch (_) {
    return value;
  }
};

const toDateInputValue = (value: Date) => format(value, "yyyy-MM-dd");

const clampDateString = (
  value: string,
  minimumDate?: string | null,
  maximumDate?: string | null,
) => {
  if (minimumDate && value < minimumDate) {
    return minimumDate;
  }

  if (maximumDate && value > maximumDate) {
    return maximumDate;
  }

  return value;
};

const roundTo = (value: number, digits = 1) =>
  Number.isFinite(value) ? Number(value.toFixed(digits)) : 0;

const formatSignedPercent = (value: number) =>
  `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;

const getMedian = (values: number[]) => {
  if (values.length === 0) return null;

  const sorted = [...values].sort((left, right) => left - right);
  const midpoint = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[midpoint - 1] + sorted[midpoint]) / 2;
  }

  return sorted[midpoint];
};

const resolveDomain = (
  values: number[],
  definition: MetricDefinition,
): [number, number] => {
  if (definition.fixedDomain) {
    return definition.fixedDomain;
  }

  if (values.length === 0) {
    return [0, 100];
  }

  const min = Math.min(...values);
  const max = Math.max(...values);

  if (min === max) {
    const padding = min === 0 ? 5 : Math.max(Math.abs(min) * 0.15, 5);
    return [Math.max(0, min - padding), max + padding];
  }

  const padding = Math.max((max - min) * 0.15, 1);
  return [Math.max(0, min - padding), max + padding];
};

const resolveMetricBands = (
  definition: MetricDefinition,
  domain: [number, number],
) => {
  const [domainMin, domainMax] = domain;

  return (definition.bands || [])
    .map((band) => {
      const resolvedFrom = Math.max(domainMin, band.from ?? domainMin);
      const resolvedTo = Math.min(domainMax, band.to ?? domainMax);

      return {
        ...band,
        resolvedFrom,
        resolvedTo,
      };
    })
    .filter((band) => band.resolvedTo > band.resolvedFrom);
};

const findMetricBandForValue = <
  TBand extends {
    resolvedFrom: number;
    resolvedTo: number;
  },
>(
  value: number,
  bands: TBand[],
) =>
  bands.find(
    (band, index) =>
      value >= band.resolvedFrom &&
      (index === bands.length - 1
        ? value <= band.resolvedTo
        : value < band.resolvedTo),
  ) || null;

const buildClampedZoomRange = (
  requestedStartIndex: number,
  windowSize: number,
  totalPoints: number,
): ZoomRange | null => {
  if (totalPoints <= 0 || windowSize >= totalPoints) {
    return null;
  }

  const clampedStartIndex = Math.max(
    0,
    Math.min(requestedStartIndex, totalPoints - windowSize),
  );

  return {
    startIndex: clampedStartIndex,
    endIndex: clampedStartIndex + windowSize - 1,
  };
};

const shiftZoomRange = (
  currentRange: ZoomRange | null,
  deltaPoints: number,
  totalPoints: number,
): ZoomRange | null => {
  if (!currentRange) {
    return null;
  }

  const windowSize =
    currentRange.endIndex - currentRange.startIndex + 1;

  return buildClampedZoomRange(
    currentRange.startIndex + deltaPoints,
    windowSize,
    totalPoints,
  );
};

const areZoomRangesEqual = (
  left: ZoomRange | null | undefined,
  right: ZoomRange | null | undefined,
) => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.startIndex === right.startIndex && left.endIndex === right.endIndex
  );
};

const downsampleMetricChartData = (
  data: MetricChartPoint[],
  targetPointCount: number,
) => {
  if (data.length <= targetPointCount || targetPointCount < 4) {
    return data;
  }

  const bucketCount = Math.max(1, Math.floor(targetPointCount / 4));
  const bucketSize = data.length / bucketCount;
  const reduced: MetricChartPoint[] = [];
  const seenRawIndexes = new Set<number>();

  for (let bucketIndex = 0; bucketIndex < bucketCount; bucketIndex += 1) {
    const startIndex = Math.floor(bucketIndex * bucketSize);
    const endIndex = Math.min(
      data.length,
      Math.floor((bucketIndex + 1) * bucketSize),
    );
    const bucket = data.slice(startIndex, Math.max(startIndex + 1, endIndex));

    if (bucket.length === 0) {
      continue;
    }

    const pointsToKeep: MetricChartPoint[] = [bucket[0]];
    const numericPoints = bucket.filter(
      (point): point is MetricChartPoint & { value: number } => point.value != null,
    );

    if (numericPoints.length > 0) {
      let minimumPoint = numericPoints[0];
      let maximumPoint = numericPoints[0];

      numericPoints.forEach((point) => {
        if (point.value < minimumPoint.value) {
          minimumPoint = point;
        }

        if (point.value > maximumPoint.value) {
          maximumPoint = point;
        }
      });

      pointsToKeep.push(minimumPoint, maximumPoint);
    }

    if (bucket[bucket.length - 1].rawIndex !== bucket[0].rawIndex) {
      pointsToKeep.push(bucket[bucket.length - 1]);
    }

    pointsToKeep
      .sort((left, right) => left.rawIndex - right.rawIndex)
      .forEach((point) => {
        if (seenRawIndexes.has(point.rawIndex)) {
          return;
        }

        seenRawIndexes.add(point.rawIndex);
        reduced.push(point);
      });
  }

  return reduced.length > 0 ? reduced : data;
};

const StudyVisualizationDashboard: React.FC = () => {
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();

  const [studyMeta, setStudyMeta] = useState<
    ParticipantOptionsResponse["meta"] | null
  >(null);
  const [participantOptions, setParticipantOptions] = useState<
    ParticipantOption[]
  >([]);
  const [selectedParticipantId, setSelectedParticipantId] = useState("");
  const [selectedMetricKey, setSelectedMetricKey] =
    useState<SelectedMetricKey>("");
  const [appliedRequest, setAppliedRequest] =
    useState<VisualizationRequest | null>(null);
  const [participantDetail, setParticipantDetail] =
    useState<ParticipantDetailResponse | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMetricSelectOpen, setIsMetricSelectOpen] = useState(false);
  const [zoomRange, setZoomRange] = useState<ZoomRange | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 29), "yyyy-MM-dd"),
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const hasInvalidDateRange = startDate > endDate;

  const optionsRequestIdRef = useRef(0);
  const detailRequestIdRef = useRef(0);
  const selectedParticipantIdRef = useRef(selectedParticipantId);
  const hoveredAbsoluteIndexRef = useRef<number | null>(null);
  const zoomRangeRef = useRef<ZoomRange | null>(null);
  const pendingZoomRangeRef = useRef<ZoomRange | null | undefined>(undefined);
  const chartInteractionFrameRef = useRef<number | null>(null);
  const chartPanRef = useRef<{
    pointerId: number;
    startClientX: number;
    initialRange: ZoomRange;
  } | null>(null);

  useEffect(() => {
    selectedParticipantIdRef.current = selectedParticipantId;
  }, [selectedParticipantId]);

  useEffect(() => {
    zoomRangeRef.current = zoomRange;
  }, [zoomRange]);

  const cancelScheduledChartInteraction = useCallback(() => {
    if (chartInteractionFrameRef.current != null) {
      window.cancelAnimationFrame(chartInteractionFrameRef.current);
      chartInteractionFrameRef.current = null;
    }

    pendingZoomRangeRef.current = undefined;
  }, []);

  const applyZoomRangeNow = useCallback(
    (nextRange: ZoomRange | null) => {
      cancelScheduledChartInteraction();
      zoomRangeRef.current = nextRange;
      setZoomRange(nextRange);
    },
    [cancelScheduledChartInteraction],
  );

  const queueZoomRangeUpdate = useCallback((nextRange: ZoomRange | null) => {
    if (areZoomRangesEqual(zoomRangeRef.current, nextRange)) {
      return;
    }

    zoomRangeRef.current = nextRange;
    pendingZoomRangeRef.current = nextRange;

    if (chartInteractionFrameRef.current != null) {
      return;
    }

    chartInteractionFrameRef.current = window.requestAnimationFrame(() => {
      chartInteractionFrameRef.current = null;
      setZoomRange(pendingZoomRangeRef.current ?? null);
      pendingZoomRangeRef.current = undefined;
    });
  }, []);

  useEffect(
    () => () => {
      cancelScheduledChartInteraction();
    },
    [cancelScheduledChartInteraction],
  );

  const loadParticipantOptions = useCallback(async () => {
    if (!studyId) return;

    const requestId = ++optionsRequestIdRef.current;
    setOptionsLoading(true);

    try {
      const response = (await apiService.get(
        `${API_ENDPOINTS.STUDY_VISUALIZATIONS.PARTICIPANT_OPTIONS}?studyId=${encodeURIComponent(
          studyId,
        )}`,
      )) as ParticipantOptionsResponse;

      if (requestId !== optionsRequestIdRef.current) {
        return;
      }

      setStudyMeta(response.meta);
      setParticipantOptions(response.items || []);

      if (!response.items?.length) {
        detailRequestIdRef.current += 1;
        setSelectedParticipantId("");
        setAppliedRequest(null);
        setParticipantDetail(null);
        hoveredAbsoluteIndexRef.current = null;
        applyZoomRangeNow(null);
        return;
      }

      const currentParticipantId = selectedParticipantIdRef.current;
      const nextParticipantId =
        currentParticipantId &&
        response.items.some((item) => item.participantId === currentParticipantId)
          ? currentParticipantId
          : "";

      setSelectedParticipantId(nextParticipantId);

      if (!nextParticipantId) {
        detailRequestIdRef.current += 1;
        setAppliedRequest(null);
        setParticipantDetail(null);
        hoveredAbsoluteIndexRef.current = null;
        applyZoomRangeNow(null);
      }
    } catch (err: any) {
      if (requestId !== optionsRequestIdRef.current) {
        return;
      }
      setError(err.message || "Failed to load participants for this study");
    } finally {
      if (requestId === optionsRequestIdRef.current) {
        setOptionsLoading(false);
      }
    }
  }, [applyZoomRangeNow, studyId]);

  const loadParticipantDetail = useCallback(
    async (request: VisualizationRequest) => {
      if (!studyId) return;

      const requestId = ++detailRequestIdRef.current;
      setDetailLoading(true);
      setParticipantDetail(null);

      try {
        const query = new URLSearchParams({
          studyId,
          participantId: request.participantId,
          metricKey: request.metricKey,
          startDate: request.startDate,
          endDate: request.endDate,
        });

        const response = (await apiService.get(
          `${API_ENDPOINTS.STUDY_VISUALIZATIONS.PARTICIPANT_DETAIL}?${query.toString()}`,
        )) as ParticipantDetailResponse;

        if (requestId !== detailRequestIdRef.current) {
          return;
        }

        setParticipantDetail(response);
      } catch (err: any) {
        if (requestId !== detailRequestIdRef.current) {
          return;
        }
        setError(err.message || "Failed to load participant visualization");
      } finally {
        if (requestId === detailRequestIdRef.current) {
          setDetailLoading(false);
        }
      }
    },
    [studyId],
  );

  useEffect(() => {
    if (!studyId) return;
    setError(null);
    detailRequestIdRef.current += 1;
    setParticipantDetail(null);
    setAppliedRequest(null);
    hoveredAbsoluteIndexRef.current = null;
    applyZoomRangeNow(null);
    loadParticipantOptions();
  }, [applyZoomRangeNow, studyId, loadParticipantOptions]);

  useEffect(() => {
    if (!studyId || !appliedRequest) {
      return;
    }

    setError(null);
    loadParticipantDetail(appliedRequest);
  }, [studyId, appliedRequest, loadParticipantDetail]);

  useEffect(() => {
    hoveredAbsoluteIndexRef.current = null;
    chartPanRef.current = null;
    setIsPanning(false);
    applyZoomRangeNow(null);
  }, [applyZoomRangeNow, participantDetail]);

  const selectedParticipant = useMemo(
    () =>
      participantOptions.find(
        (participant) => participant.participantId === selectedParticipantId,
      ) || null,
    [participantOptions, selectedParticipantId],
  );
  const selectedParticipantAvailableFrom =
    selectedParticipant?.availableFrom || null;
  const selectedParticipantAvailableTo = selectedParticipant?.availableTo || null;

  const appliedParticipant = useMemo(
    () =>
      appliedRequest
        ? participantOptions.find(
            (participant) =>
              participant.participantId === appliedRequest.participantId,
          ) || null
        : null,
    [appliedRequest, participantOptions],
  );

  const appliedMetricDefinition = appliedRequest
    ? METRIC_DEFINITIONS[appliedRequest.metricKey]
    : null;

  const metricChartData = useMemo(
    () =>
      !appliedMetricDefinition
        ? []
        : (participantDetail?.daily || []).map((day, index) => ({
            date: day.date,
            value: day.metricValue,
            rawIndex: index,
          })),
    [participantDetail, appliedMetricDefinition],
  );

  const metricValues = useMemo(
    () =>
      metricChartData
        .map((item) => item.value)
        .filter((value): value is number => value != null),
    [metricChartData],
  );

  const metricStats = useMemo(() => {
    if (metricValues.length === 0) {
      return null;
    }

    const average =
      metricValues.reduce((total, value) => total + value, 0) /
      metricValues.length;
    const latest = [...metricChartData]
      .reverse()
      .find((item) => item.value != null);

    return {
      count: metricValues.length,
      minimum: Math.min(...metricValues),
      maximum: Math.max(...metricValues),
      average,
      median: getMedian(metricValues),
      latest: latest?.value ?? null,
      latestDate: latest?.date ?? null,
    };
  }, [metricChartData, metricValues]);

  const chartDomain = useMemo(
    () =>
      appliedMetricDefinition
        ? resolveDomain(metricValues, appliedMetricDefinition)
        : [0, 100],
    [appliedMetricDefinition, metricValues],
  );
  const resolvedMetricBands = useMemo(
    () =>
      appliedMetricDefinition
        ? resolveMetricBands(
            appliedMetricDefinition,
            chartDomain as [number, number],
          )
        : [],
    [appliedMetricDefinition, chartDomain],
  );
  const absoluteZoneInsight = useMemo<MetricInsight | null>(() => {
    if (!appliedMetricDefinition || !metricStats || metricStats.latest == null) {
      return null;
    }

    const matchingBand = findMetricBandForValue(
      metricStats.latest,
      resolvedMetricBands,
    );

    if (!matchingBand) {
      return {
        title: "Absolute Zone",
        label: "Baseline-first",
        detail:
          appliedMetricDefinition.absoluteRangeNote ||
          `No universal good or bad threshold is used for ${appliedMetricDefinition.shortLabel.toLowerCase()}.`,
        tone: "neutral",
      };
    }

    return {
      title: "Absolute Zone",
      label: matchingBand.label,
      detail: `${appliedMetricDefinition.formatter(metricStats.latest)} falls in the ${matchingBand.label.toLowerCase()} range (${matchingBand.rangeLabel}). ${matchingBand.meaning}.`,
      tone: matchingBand.tone,
    };
  }, [
    appliedMetricDefinition,
    metricStats,
    resolvedMetricBands,
  ]);
  const baselineInsight = useMemo<MetricInsight | null>(() => {
    if (!appliedMetricDefinition || !metricStats || metricStats.latest == null) {
      return null;
    }

    const baseline = metricStats.average;
    if (!Number.isFinite(baseline) || baseline === 0) {
      return {
        title: "Personal Baseline",
        label: "Not enough context",
        detail:
          appliedMetricDefinition.baselineNote ||
          "A baseline comparison needs more non-null daily values in the selected range.",
        tone: "neutral",
      };
    }

    const percentDelta = ((metricStats.latest - baseline) / Math.abs(baseline)) * 100;
    const absolutePercentDelta = Math.abs(percentDelta);
    const baselineDirection =
      absolutePercentDelta < 10
        ? "within"
        : percentDelta > 0
          ? "above"
          : "below";
    const tone =
      baselineDirection === "within"
        ? "neutral"
        : appliedMetricDefinition.baselinePreference === "higher"
          ? baselineDirection === "above"
            ? "good"
            : "caution"
          : appliedMetricDefinition.baselinePreference === "lower"
            ? baselineDirection === "below"
              ? "good"
              : "caution"
            : "neutral";
    const label =
      baselineDirection === "within"
        ? "Within baseline"
        : baselineDirection === "above"
          ? "Above baseline ↑"
          : "Below baseline ↓";
    const detail =
      baselineDirection === "within"
        ? `Most recent ${appliedMetricDefinition.formatter(metricStats.latest)} is within 10% of the selected-range baseline (${appliedMetricDefinition.formatter(baseline)}).`
        : `Most recent ${appliedMetricDefinition.formatter(metricStats.latest)} is ${formatSignedPercent(percentDelta)} versus the selected-range baseline (${appliedMetricDefinition.formatter(baseline)}).`;

    return {
      title: "Personal Baseline",
      label,
      detail: `${detail} ${
        appliedMetricDefinition.baselineNote ||
        "Baseline here uses this participant's average within the selected date range."
      }`,
      tone,
    };
  }, [appliedMetricDefinition, metricStats]);
  const trendInsight = useMemo<MetricInsight | null>(() => {
    if (!appliedMetricDefinition) {
      return null;
    }

    const nonNullSeries = metricChartData.filter(
      (item): item is MetricChartPoint & { value: number } => item.value != null,
    );

    if (nonNullSeries.length < 8) {
      return {
        title: "Recent Trend",
        label: "Need more data",
        detail:
          "Trend signals appear once there are enough non-null values to compare the recent window with the immediately preceding window.",
        tone: "neutral",
      };
    }

    const windowSize = Math.min(7, Math.max(3, Math.floor(nonNullSeries.length / 3)));
    const recentWindow = nonNullSeries.slice(-windowSize);
    const priorWindow = nonNullSeries.slice(-windowSize * 2, -windowSize);

    if (priorWindow.length < windowSize) {
      return {
        title: "Recent Trend",
        label: "Need more data",
        detail:
          "Trend signals appear once there are enough non-null values to compare two adjacent windows.",
        tone: "neutral",
      };
    }

    const recentAverage =
      recentWindow.reduce((total, item) => total + item.value, 0) /
      recentWindow.length;
    const priorAverage =
      priorWindow.reduce((total, item) => total + item.value, 0) /
      priorWindow.length;
    const percentDelta =
      priorAverage === 0
        ? 0
        : ((recentAverage - priorAverage) / Math.abs(priorAverage)) * 100;
    const absolutePercentDelta = Math.abs(percentDelta);

    if (absolutePercentDelta < 5) {
      return {
        title: "Recent Trend",
        label: "Stable",
        detail: `The latest ${windowSize} data points are broadly stable versus the prior ${windowSize}.`,
        tone: "neutral",
      };
    }

    const movedUp = percentDelta > 0;
    const label =
      appliedMetricDefinition.baselinePreference === "higher"
        ? movedUp
          ? "Improving"
          : "Declining"
        : appliedMetricDefinition.baselinePreference === "lower"
          ? movedUp
            ? "Elevating"
            : "Recovering"
          : movedUp
            ? "Trending up"
            : "Trending down";
    const tone =
      appliedMetricDefinition.baselinePreference === "higher"
        ? movedUp
          ? "good"
          : "caution"
        : appliedMetricDefinition.baselinePreference === "lower"
          ? movedUp
            ? "caution"
            : "good"
          : movedUp
            ? "caution"
            : "good";

    return {
      title: "Recent Trend",
      label,
      detail: `Average of the latest ${windowSize} data points is ${formatSignedPercent(percentDelta)} versus the previous ${windowSize}.`,
      tone,
    };
  }, [appliedMetricDefinition, metricChartData]);
  const metricInsights = useMemo(
    () =>
      [absoluteZoneInsight, baselineInsight, trendInsight].filter(
        (item): item is MetricInsight => Boolean(item),
      ),
    [absoluteZoneInsight, baselineInsight, trendInsight],
  );
  const todayDateStr = useMemo(() => toDateInputValue(new Date()), []);
  const selectedParticipantReferenceEndDate =
    selectedParticipantAvailableTo || todayDateStr;

  useEffect(() => {
    if (!selectedParticipantId) {
      return;
    }

    const nextEndDate = clampDateString(
      endDate,
      selectedParticipantAvailableFrom,
      selectedParticipantReferenceEndDate,
    );
    const nextStartDate = clampDateString(
      startDate,
      selectedParticipantAvailableFrom,
      nextEndDate,
    );

    if (nextEndDate !== endDate) {
      setEndDate(nextEndDate);
    }

    if (nextStartDate !== startDate) {
      setStartDate(nextStartDate);
    }
  }, [
    endDate,
    selectedParticipantAvailableFrom,
    selectedParticipantId,
    selectedParticipantReferenceEndDate,
    startDate,
  ]);

  const canVisualize = Boolean(
    studyId && selectedParticipantId && selectedMetricKey && !hasInvalidDateRange,
  );
  const hasPendingChanges = Boolean(
    appliedRequest &&
      (appliedRequest.participantId !== selectedParticipantId ||
        appliedRequest.metricKey !== selectedMetricKey ||
        appliedRequest.startDate !== startDate ||
        appliedRequest.endDate !== endDate),
  );
  const visibleChartRange = zoomRange || {
    startIndex: 0,
    endIndex: Math.max(metricChartData.length - 1, 0),
  };
  const visibleMetricChartData = useMemo(
    () =>
      metricChartData.slice(
        visibleChartRange.startIndex,
        visibleChartRange.endIndex + 1,
      ),
    [metricChartData, visibleChartRange.endIndex, visibleChartRange.startIndex],
  );
  const isZoomed = Boolean(
    zoomRange &&
      (zoomRange.startIndex > 0 ||
        zoomRange.endIndex < Math.max(metricChartData.length - 1, 0)),
  );
  const renderedMetricChartData = useMemo(
    () =>
      downsampleMetricChartData(
        visibleMetricChartData,
        isZoomed ? 520 : 320,
      ),
    [isZoomed, visibleMetricChartData],
  );
  const canPanLeft = Boolean(zoomRange && zoomRange.startIndex > 0);
  const canPanRight = Boolean(
    zoomRange &&
      zoomRange.endIndex < Math.max(metricChartData.length - 1, 0),
  );
  const shouldRenderPointDots = renderedMetricChartData.length <= 140;
  const chartLineType = renderedMetricChartData.length > 360 ? "linear" : "monotone";
  const axisShouldIncludeYear = useMemo(() => {
    if (!appliedRequest) {
      return false;
    }

    try {
      return (
        new Date(`${appliedRequest.startDate}T00:00:00`).getFullYear() !==
        new Date(`${appliedRequest.endDate}T00:00:00`).getFullYear()
      );
    } catch (_) {
      return true;
    }
  }, [appliedRequest]);
  const visibleStartDate = visibleMetricChartData[0]?.date || null;
  const visibleEndDate =
    visibleMetricChartData[visibleMetricChartData.length - 1]?.date || null;
  const visualizedParticipantName = useMemo(() => {
    if (appliedParticipant) {
      return [appliedParticipant.firstName, appliedParticipant.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
    }

    if (participantDetail?.participant) {
      return [
        participantDetail.participant.firstName,
        participantDetail.participant.lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();
    }

    return "";
  }, [appliedParticipant, participantDetail]);
  const visualizedParticipantLabel =
    visualizedParticipantName || participantDetail?.participant.participantId || "";
  const showInitialLoader = optionsLoading && participantOptions.length === 0;
  const selectedParticipantAvailabilityText = useMemo(() => {
    if (!selectedParticipantId) {
      return null;
    }

    if (selectedParticipantAvailableFrom && selectedParticipantAvailableTo) {
      const baseText = `Showing data available from: ${formatDateLabel(
        selectedParticipantAvailableFrom,
        true,
      )} to ${formatDateLabel(selectedParticipantAvailableTo, true)}`;

      if (selectedParticipant?.availabilityStatus === "historical_in_progress") {
        return `${baseText}. Historical extraction is still in progress, so older dates may appear as more history is loaded.`;
      }

      if (selectedParticipant?.availabilityStatus === "historical_complete") {
        return `${baseText}. Historical extraction is complete for this participant.`;
      }

      return baseText;
    }

    return "Showing data available from: No synced Fitbit summary data found yet for this participant.";
  }, [
    selectedParticipant?.availabilityStatus,
    selectedParticipantAvailableFrom,
    selectedParticipantAvailableTo,
    selectedParticipantId,
  ]);

  const panStepSize = zoomRange
    ? Math.max(1, Math.round((zoomRange.endIndex - zoomRange.startIndex + 1) * 0.35))
    : 0;

  const handleVisualize = useCallback(() => {
    if (!studyId || !selectedParticipantId || !selectedMetricKey) {
      return;
    }

    if (hasInvalidDateRange) {
      setError("Start date must be on or before end date.");
      return;
    }

    setError(null);
    setAppliedRequest({
      participantId: selectedParticipantId,
      metricKey: selectedMetricKey,
      startDate,
      endDate,
    });
  }, [
    endDate,
    hasInvalidDateRange,
    selectedMetricKey,
    selectedParticipantId,
    startDate,
    studyId,
  ]);

  const applyPresetDateRange = useCallback(
    (preset: "7d" | "30d" | "6m" | "1y" | "5y" | "all") => {
      const referenceEndDate = selectedParticipantReferenceEndDate;

      if (preset === "all") {
        if (!selectedParticipantAvailableFrom || !selectedParticipantAvailableTo) {
          return;
        }

        setStartDate(selectedParticipantAvailableFrom);
        setEndDate(selectedParticipantAvailableTo);
        return;
      }

      const referenceEnd = new Date(`${referenceEndDate}T00:00:00`);
      const rawStartDate =
        preset === "7d"
          ? subDays(referenceEnd, 6)
          : preset === "30d"
            ? subDays(referenceEnd, 29)
            : preset === "6m"
              ? subMonths(referenceEnd, 6)
              : preset === "1y"
                ? subYears(referenceEnd, 1)
                : subYears(referenceEnd, 5);

      const nextStartDate = clampDateString(
        toDateInputValue(rawStartDate),
        selectedParticipantAvailableFrom,
        referenceEndDate,
      );

      setStartDate(nextStartDate);
      setEndDate(referenceEndDate);
    },
    [
      selectedParticipantAvailableFrom,
      selectedParticipantAvailableTo,
      selectedParticipantReferenceEndDate,
    ],
  );

  const handleChartMouseMove = useCallback(
    (state: { activeTooltipIndex?: number | string | null }) => {
      if (isPanning) {
        return;
      }

      const nextTooltipIndex =
        typeof state?.activeTooltipIndex === "number"
          ? state.activeTooltipIndex
          : typeof state?.activeTooltipIndex === "string"
            ? Number(state.activeTooltipIndex)
            : Number.NaN;

      if (!Number.isFinite(nextTooltipIndex)) {
        return;
      }

      hoveredAbsoluteIndexRef.current =
        renderedMetricChartData[nextTooltipIndex]?.rawIndex ??
        visibleChartRange.startIndex + nextTooltipIndex;
    },
    [isPanning, renderedMetricChartData, visibleChartRange.startIndex],
  );

  const handlePanWindow = useCallback(
    (deltaPoints: number) => {
      if (!zoomRange || deltaPoints === 0) {
        return;
      }

      hoveredAbsoluteIndexRef.current = null;
      applyZoomRangeNow(
        shiftZoomRange(zoomRangeRef.current, deltaPoints, metricChartData.length),
      );
    },
    [applyZoomRangeNow, metricChartData.length, zoomRange],
  );

  const handleChartWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (metricChartData.length <= 1) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const totalPoints = metricChartData.length;
      const currentWindowSize = zoomRange
        ? zoomRange.endIndex - zoomRange.startIndex + 1
        : totalPoints;
      const hasHorizontalPanIntent =
        isZoomed && Math.abs(event.deltaX) > Math.abs(event.deltaY);

      if (hasHorizontalPanIntent) {
        const deltaPoints = Math.round(
          (event.deltaX / 120) * Math.max(1, currentWindowSize * 0.2),
        );

        if (deltaPoints !== 0) {
          hoveredAbsoluteIndexRef.current = null;
          queueZoomRangeUpdate(
            shiftZoomRange(zoomRangeRef.current, deltaPoints, totalPoints),
          );
        }
        return;
      }

      if (event.deltaY === 0) {
        return;
      }

      const minVisiblePoints = Math.min(
        totalPoints,
        Math.max(7, Math.round(totalPoints * 0.08)),
      );

      const currentRange = zoomRangeRef.current;
      const nextRange = (() => {
        const baseRange = currentRange || {
          startIndex: 0,
          endIndex: totalPoints - 1,
        };
        const currentWindow =
          baseRange.endIndex - baseRange.startIndex + 1;
        const anchorIndex =
          hoveredAbsoluteIndexRef.current == null
            ? Math.round((baseRange.startIndex + baseRange.endIndex) / 2)
            : Math.min(
                baseRange.endIndex,
                Math.max(baseRange.startIndex, hoveredAbsoluteIndexRef.current),
              );

        if (event.deltaY < 0 && currentWindow <= minVisiblePoints) {
          return baseRange;
        }

        const nextWindow =
          event.deltaY < 0
            ? Math.max(minVisiblePoints, Math.floor(currentWindow * 0.75))
            : Math.min(totalPoints, Math.ceil(currentWindow * 1.25));

        if (nextWindow >= totalPoints) {
          return null;
        }

        const anchorRatio =
          currentWindow <= 1
            ? 0.5
            : (anchorIndex - baseRange.startIndex) / (currentWindow - 1);

        let nextStart = Math.round(
          anchorIndex - anchorRatio * (nextWindow - 1),
        );
        let nextEnd = nextStart + nextWindow - 1;

        if (nextStart < 0) {
          nextEnd += -nextStart;
          nextStart = 0;
        }

        if (nextEnd > totalPoints - 1) {
          const overflow = nextEnd - (totalPoints - 1);
          nextStart = Math.max(0, nextStart - overflow);
          nextEnd = totalPoints - 1;
        }

        if (nextStart === 0 && nextEnd === totalPoints - 1) {
          return null;
        }

        return {
          startIndex: nextStart,
          endIndex: nextEnd,
        };
      })();

      hoveredAbsoluteIndexRef.current = null;
      queueZoomRangeUpdate(nextRange);
    },
    [isZoomed, metricChartData.length, queueZoomRangeUpdate, zoomRange],
  );

  const handleChartPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!zoomRange || metricChartData.length <= 1) {
        return;
      }

      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture?.(event.pointerId);

      chartPanRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        initialRange: zoomRange,
      };
      setIsPanning(true);
      hoveredAbsoluteIndexRef.current = null;
    },
    [metricChartData.length, zoomRange],
  );

  const handleChartPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const panState = chartPanRef.current;
      if (!panState || panState.pointerId !== event.pointerId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const surfaceWidth = event.currentTarget.clientWidth;
      if (surfaceWidth <= 0) {
        return;
      }

      const windowSize =
        panState.initialRange.endIndex - panState.initialRange.startIndex + 1;
      const deltaX = event.clientX - panState.startClientX;
      const deltaPoints = Math.round((-deltaX / surfaceWidth) * windowSize);

      queueZoomRangeUpdate(
        buildClampedZoomRange(
          panState.initialRange.startIndex + deltaPoints,
          windowSize,
          metricChartData.length,
        ),
      );
    },
    [metricChartData.length, queueZoomRangeUpdate],
  );

  const finishChartPan = useCallback(
    (pointerId: number, currentTarget: HTMLDivElement) => {
      if (chartPanRef.current?.pointerId !== pointerId) {
        return;
      }

      currentTarget.releasePointerCapture?.(pointerId);
      chartPanRef.current = null;
      setIsPanning(false);
    },
    [],
  );

  const handleChartPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      finishChartPan(event.pointerId, event.currentTarget);
    },
    [finishChartPan],
  );

  const handleChartPointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      finishChartPan(event.pointerId, event.currentTarget);
    },
    [finishChartPan],
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(15, 118, 110, 0.10) 0%, rgba(37, 99, 235, 0.08) 100%)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() =>
            studyId
              ? navigate(`/researcher/study/${studyId}`)
              : navigate("/researcher/dashboard")
          }
          sx={{ mb: 2, px: 0 }}
        >
          Back to Study
        </Button>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {studyMeta?.studyName || "Participant Visualizations"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Choose a participant from this study, choose a metric, then
              inspect one daily Fitbit metric at a time.
            </Typography>
          </Box>

          {studyMeta?.studyCode && (
            <Chip
              label={`Study Code: ${studyMeta.studyCode}`}
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
      </Box>

      {!studyId && (
        <Alert severity="warning">
          Missing study reference in the URL. Please open this page from a study
          dashboard.
        </Alert>
      )}

      {studyId && (
        <>
          <Card
            elevation={0}
            sx={{
              mb: 3,
              border: "1px solid",
              borderColor: "divider",
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 250, 252, 0.98) 100%)",
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Visualization Controls
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Participants are scoped to the current study automatically.
                  We only request the selected metric after you click
                  Visualize.
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "minmax(280px, 1.5fr) repeat(3, minmax(160px, 1fr))",
                    },
                    gap: 2,
                    alignItems: "end",
                  }}
                >
                  <Autocomplete
                    options={participantOptions}
                    value={selectedParticipant}
                    loading={optionsLoading}
                    onChange={(_, option) =>
                      setSelectedParticipantId(option?.participantId || "")
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.participantId === value.participantId
                    }
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Participant"
                        placeholder="Choose a participant"
                        size="small"
                      />
                    )}
                  />

                  <TextField
                    select
                    label="Metric"
                    value={selectedMetricKey}
                    onChange={(event) =>
                      setSelectedMetricKey(
                        event.target.value as SelectedMetricKey,
                      )
                    }
                    size="small"
                    InputLabelProps={{
                      shrink: Boolean(selectedMetricKey) || isMetricSelectOpen,
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      onOpen: () => setIsMetricSelectOpen(true),
                      onClose: () => setIsMetricSelectOpen(false),
                      renderValue: (value) => {
                        if (value) {
                          return METRIC_DEFINITIONS[value as MetricKey].shortLabel;
                        }

                        return isMetricSelectOpen ? METRIC_SELECT_PLACEHOLDER : "";
                      },
                    }}
                  >
                    <MenuItem value="">{METRIC_SELECT_PLACEHOLDER}</MenuItem>
                    {SUPPORTED_METRIC_GROUPS.flatMap((group) => [
                      <ListSubheader key={`${group.label}-header`}>
                        {group.label}
                      </ListSubheader>,
                      ...group.keys.map((metricKey) => (
                        <MenuItem key={metricKey} value={metricKey}>
                          {METRIC_DEFINITIONS[metricKey].shortLabel}
                        </MenuItem>
                      )),
                    ])}
                  </TextField>

                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    inputProps={{
                      min: selectedParticipantAvailableFrom || undefined,
                      max: endDate,
                    }}
                  />

                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    inputProps={{
                      min: selectedParticipantAvailableFrom || startDate,
                      max: selectedParticipantReferenceEndDate,
                    }}
                  />
                </Box>

                {selectedParticipantAvailabilityText && (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        maxWidth: 760,
                      }}
                    >
                      {selectedParticipantAvailabilityText}
                    </Typography>
                  </Box>
                )}

                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  justifyContent="center"
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applyPresetDateRange("7d")}
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applyPresetDateRange("30d")}
                  >
                    Last 30 Days
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applyPresetDateRange("6m")}
                  >
                    Last 6 Months
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applyPresetDateRange("1y")}
                  >
                    Last 1 Year
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applyPresetDateRange("5y")}
                  >
                    Last 5 Years
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applyPresetDateRange("all")}
                    disabled={
                      !selectedParticipantAvailableFrom ||
                      !selectedParticipantAvailableTo
                    }
                  >
                    All Time
                  </Button>
                </Stack>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleVisualize}
                    disabled={!canVisualize || detailLoading}
                    sx={{
                      minWidth: { xs: "100%", sm: 220 },
                    }}
                  >
                    {detailLoading ? "Visualizing..." : "Visualize"}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {hasInvalidDateRange && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Start date must be on or before end date.
            </Alert>
          )}

          {hasPendingChanges && !detailLoading && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Selections changed. Click Visualize to update the chart.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {detailLoading && <LinearProgress sx={{ mb: 3, borderRadius: 999 }} />}

          {showInitialLoader ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress />
            </Box>
          ) : participantOptions.length === 0 ? (
            <Alert severity="info">
              No participants were found for this study yet.
            </Alert>
          ) : !appliedRequest ? (
            !selectedParticipantId && !selectedMetricKey ? (
              <Alert severity="info">
                Choose a participant, choose a metric, then click Visualize to
                load the chart.
              </Alert>
            ) : !selectedParticipantId ? (
              <Alert severity="info">
                Choose a participant to begin visualizing daily Fitbit data.
              </Alert>
            ) : !selectedMetricKey ? (
              <Alert severity="info">
                Choose a metric to generate the chart.
              </Alert>
            ) : (
              <Alert severity="info">
                Selections are ready. Click Visualize to load the chart.
              </Alert>
            )
          ) : !participantDetail ? (
            detailLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
              </Box>
            ) : null
          ) : appliedMetricDefinition ? (
            <Card
              elevation={0}
              sx={{
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                background:
                  "linear-gradient(180deg, rgba(252, 255, 254, 0.98) 0%, rgba(246, 248, 251, 0.98) 100%)",
                boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2.5, md: 3.5 },
                  py: 3,
                  borderBottom: "1px solid",
                  borderBottomColor: "divider",
                  background:
                    "linear-gradient(180deg, rgba(240, 253, 250, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", lg: "center" }}
                >
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: 1.6,
                        color: "text.secondary",
                      }}
                    >
                      Daily Visualization
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "text.primary" }}
                    >
                      {appliedMetricDefinition.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "text.secondary", mt: 0.75, maxWidth: 720 }}
                    >
                      {appliedMetricDefinition.description}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Chip
                      label={`Range: ${appliedRequest.startDate} to ${appliedRequest.endDate}`}
                      variant="outlined"
                    />
                    <Chip
                      label={`Generated: ${formatTimestampLabel(
                        participantDetail.meta.generatedAt,
                      )}`}
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              </Box>

              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "rgba(255, 255, 255, 0.92)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Metric
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mt: 0.75, color: "text.primary" }}
                    >
                      {appliedMetricDefinition.shortLabel}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "rgba(236, 253, 245, 0.86)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Total Points
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, mt: 0.75, color: "#0f766e" }}
                    >
                      {metricStats?.count ?? 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Daily summary values in the selected range
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "rgba(248, 250, 252, 0.95)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Participant
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mt: 0.75, color: "text.primary" }}
                    >
                      {visualizedParticipantLabel}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "#334155",
                        fontFamily:
                          "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                      }}
                    >
                      {participantDetail.participant.participantId}
                    </Typography>
                  </Box>
                </Box>

                {metricStats ? (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, textAlign: "center", mb: 1.5 }}
                      >
                        Research Context
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", mb: 2 }}
                      >
                        Absolute zones are wearable reference ranges. Personal
                        baseline compares the most recent value to this
                        participant&apos;s average in the selected date range,
                        and trend compares the latest window with the previous
                        one.
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(3, minmax(0, 1fr))",
                          },
                          gap: 1.5,
                        }}
                      >
                        {metricInsights.map((insight) => {
                          const toneStyle = INSIGHT_TONE_STYLES[insight.tone];

                          return (
                            <Box
                              key={insight.title}
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: toneStyle.border,
                                backgroundColor: toneStyle.background,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                minHeight: 148,
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {insight.title}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  mt: 0.75,
                                  fontWeight: 700,
                                  color: toneStyle.text,
                                }}
                              >
                                {insight.label}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ mt: 1, color: "text.secondary" }}
                              >
                                {insight.detail}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: { xs: 1.25, md: 2.5 },
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        background:
                          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 249, 255, 0.82) 100%)",
                        mb: 3,
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", lg: "row" }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", lg: "center" }}
                        sx={{ mb: 2.5 }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: "text.primary" }}
                          >
                            Trend Over Time
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Hover and use your mouse wheel to zoom. Once
                            zoomed, drag left or right or use the pan controls
                            to move through time at the same scale.
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: { xs: "stretch", lg: "flex-end" },
                            width: { xs: "100%", lg: "auto" },
                            gap: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            justifyContent={{ xs: "flex-start", lg: "flex-end" }}
                          >
                            <Chip
                              label={
                                isZoomed && visibleStartDate && visibleEndDate
                                  ? `Zoomed: ${formatDateLabel(
                                      visibleStartDate,
                                      true,
                                    )} to ${formatDateLabel(visibleEndDate, true)}`
                                  : "Showing full range"
                              }
                              variant="outlined"
                            />
                            <Chip
                              label={`Visible points: ${visibleMetricChartData.length}`}
                              variant="outlined"
                            />
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            justifyContent={{ xs: "flex-start", lg: "flex-end" }}
                            sx={{
                              visibility: isZoomed ? "visible" : "hidden",
                              minHeight: 32,
                            }}
                          >
                            <Button
                              size="small"
                              onClick={() => handlePanWindow(-panStepSize)}
                              disabled={!isZoomed || !canPanLeft}
                            >
                              Pan Left
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handlePanWindow(panStepSize)}
                              disabled={!isZoomed || !canPanRight}
                            >
                              Pan Right
                            </Button>
                            <Button
                              size="small"
                              onClick={() => {
                                hoveredAbsoluteIndexRef.current = null;
                                applyZoomRangeNow(null);
                              }}
                              disabled={!isZoomed}
                            >
                              Reset zoom
                            </Button>
                          </Stack>
                        </Box>
                      </Stack>

                      <Box
                        data-testid="metric-chart-surface"
                        onWheelCapture={handleChartWheel}
                        onPointerDownCapture={handleChartPointerDown}
                        onPointerMoveCapture={handleChartPointerMove}
                        onPointerUpCapture={handleChartPointerUp}
                        onPointerCancelCapture={handleChartPointerCancel}
                        sx={{
                          p: { xs: 1, md: 1.5 },
                          borderRadius: 3,
                          border: "1px solid",
                          borderColor: "rgba(148, 163, 184, 0.22)",
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          overflow: "hidden",
                          overscrollBehavior: "contain",
                          touchAction: "none",
                          userSelect: "none",
                          cursor:
                            isZoomed
                              ? isPanning
                                ? "grabbing"
                                : "grab"
                              : metricChartData.length > 1
                                ? "zoom-in"
                                : "default",
                        }}
                      >
                        <ResponsiveContainer width="100%" height={400}>
                          <LineChart
                            data={renderedMetricChartData}
                            margin={{ top: 20, right: 24, left: 0, bottom: 12 }}
                            onMouseMove={handleChartMouseMove}
                            onMouseLeave={() => {
                              hoveredAbsoluteIndexRef.current = null;
                            }}
                          >
                            {resolvedMetricBands.map((band, index) => (
                              <ReferenceArea
                                key={`${band.label}-${band.resolvedFrom}-${band.resolvedTo}-${index}`}
                                x1={renderedMetricChartData[0]?.date}
                                x2={
                                  renderedMetricChartData[
                                    renderedMetricChartData.length - 1
                                  ]?.date
                                }
                                y1={band.resolvedFrom}
                                y2={band.resolvedTo}
                                fill={band.color}
                                ifOverflow="extendDomain"
                              />
                            ))}
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#dbe4f0"
                            />
                            <XAxis
                              dataKey="date"
                              tickFormatter={(value) =>
                                formatDateLabel(
                                  String(value),
                                  axisShouldIncludeYear,
                                )
                              }
                              minTickGap={24}
                            />
                            <YAxis
                              domain={chartDomain}
                              tickFormatter={appliedMetricDefinition.axisFormatter}
                            />
                            <RechartsTooltip
                              isAnimationActive={false}
                              labelFormatter={(value) =>
                                formatDateLabel(String(value), true)
                              }
                              formatter={(value?: number) => [
                                value == null
                                  ? "No data"
                                  : appliedMetricDefinition.formatter(value),
                                appliedMetricDefinition.shortLabel,
                              ]}
                            />
                            <ReferenceLine
                              y={roundTo(metricStats.average, 2)}
                              stroke={appliedMetricDefinition.color}
                              strokeDasharray="4 4"
                              opacity={0.35}
                            />
                            <Line
                              type={chartLineType}
                              dataKey="value"
                              stroke={appliedMetricDefinition.color}
                              strokeWidth={3}
                              connectNulls={false}
                              isAnimationActive={false}
                              dot={
                                shouldRenderPointDots
                                  ? {
                                      r: 2.5,
                                      strokeWidth: 0,
                                      fill: appliedMetricDefinition.color,
                                    }
                                  : false
                              }
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, textAlign: "center", mb: 2 }}
                    >
                      Data Summary
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", mb: 2 }}
                    >
                      Most Recent shows the latest non-null value in the
                      selected date range.
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr 1fr",
                          md: "repeat(6, 1fr)",
                        },
                        gap: 1.5,
                      }}
                    >
                      {[
                        { label: "Data Points", value: String(metricStats.count) },
                        {
                          label: "Minimum",
                          value: appliedMetricDefinition.formatter(
                            metricStats.minimum,
                          ),
                        },
                        {
                          label: "Maximum",
                          value: appliedMetricDefinition.formatter(
                            metricStats.maximum,
                          ),
                        },
                        {
                          label: "Average",
                          value: appliedMetricDefinition.formatter(
                            metricStats.average,
                          ),
                        },
                        {
                          label: "Median",
                          value:
                            metricStats.median == null
                              ? "Not available"
                              : appliedMetricDefinition.formatter(
                                  metricStats.median,
                                ),
                        },
                        {
                          label: "Most Recent",
                          value:
                            metricStats.latest == null
                              ? "Not available"
                              : `${appliedMetricDefinition.formatter(
                                  metricStats.latest,
                                )}${
                                  metricStats.latestDate
                                    ? ` · ${metricStats.latestDate}`
                                    : ""
                                }`,
                        },
                      ].map((item) => (
                        <Box
                          key={item.label}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "rgba(255, 255, 255, 0.94)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {item.label}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 700, mt: 0.75 }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2.5 }}
                    >
                      This chart uses summary-level daily Fitbit values only.
                      Intraday values are intentionally excluded, and zooming is
                      handled locally without additional metric requests.
                    </Typography>
                  </>
                ) : (
                  <Alert severity="info">
                    No {appliedMetricDefinition.shortLabel.toLowerCase()} data
                    was found for this participant in the selected date range.
                    Try another participant, metric, or date range.
                  </Alert>
                )}
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </Container>
  );
};

export default StudyVisualizationDashboard;
